import Button from '@material-ui/core/Button';
import { FormFooter } from '../FormFooter';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import React from 'react';
import { useQuery } from 'react-query';
import { connect, useDispatch } from 'react-redux';
import { clearSubmitErrors, Field, FieldArray, submit, getFormMeta, getFormValues, reduxForm } from 'redux-form';
import FlightService from '../../../../services/flight';
import { googleDirection } from '../../../../services/googleMap';
import { FormDateTimeField, FormNumberField, FormToogleButtonField } from '../../../../components/fields';
import { FormRoutePoints } from '../../FormRoutePoints';
import styles from './style.module.css';
import validate from './validate';
import { ReactComponent as Mask } from './icons/mask-icon.svg';
import { ReactComponent as Package } from './icons/package-icon.svg';
import { ReactComponent as Paw } from './icons/paw-icon.svg';

const queryConfig = {
  retry: false,
  refetchOnWindowFocus: false,
  queryFnParamsFilter: args => args.slice(1),
};

const getRoutePath = async (origin, destination, puTime, isLiveRide) =>
  origin && destination ? await googleDirection(origin, destination, new Date(isLiveRide ? '' : puTime)) : null;


const RoutePoints = React.memo((props) => {
  const dispatch = useDispatch();
  const { formData, data } = props;

  const location = (location) => location ? `${location?.lat}, ${location?.lng}` : undefined;

  const puPoint = formData?.routePoints?.[0];
  const doPoint = formData?.routePoints?.[formData?.routePoints?.length - 1];
  const origin = location(puPoint?.address?.location);
  const destination = location(doPoint?.address?.location);
  const puTime = formData?.dateTime;
  const isLiveRide = !data?.dateTime?.visited || puTime < new Date().getTime();

  const { data: routePath } = useQuery([ 'googleDirection',
    origin,
    destination,
    puTime,
    isLiveRide,
  ], getRoutePath, queryConfig);

  React.useEffect(() => {
    props.change('routePath', routePath);
  }, [ props.change, routePath ]);


  const flight = puPoint?.airport?.flight;
  const callFlightApi = React.useMemo(() => {
    return debounce((airline, flightNumber, year, month, day) => {
      FlightService.status(airline, flightNumber, year, month, day)
        .then(({ flightStatuses }) => {
          if (!!flightStatuses && !!flightStatuses[0]?.airportResources?.arrivalTerminal) {
            props.change('routePoints[0].airport.terminal', flightStatuses[0]?.airportResources?.arrivalTerminal);
            props.change('routePoints[0].airport.noVerifiedFlight', false);
            const arrivalDate = flightStatuses[0]?.arrivalDate?.dateUtc;
            if (arrivalDate) {
              props.change('routePoints[0].airport.flight.arrivingTime', new Date(arrivalDate).getTime());
            }
          } else {
            props.change('routePoints[0].airport.noVerifiedFlight', true);
          }
        });
    }, 500);
  }, []);
  React.useEffect(() => {
    if (!flight?.number) {
      props.change('routePoints[0].airport.noVerifiedFlight', false);
    } else if (flight?.airline?.code) {
      const year = moment(puTime)
        .format('YYYY');
      const month = moment(puTime)
        .format('MM');
      const day = moment(puTime)
        .format('DD');
      callFlightApi(flight?.airline?.code, flight?.number, year, month, day);
    }
  }, [ puTime, flight?.airline?.code, flight?.number ]);

  const { handleSubmit, submitting } = props;
  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.main}>
        <div className={styles.container}>
          <div className={styles.dateBlock}>
            <Field name="dateTime"
                   component={FormDateTimeField}
                   disabled={submitting}
                   label="Date & Time"
            />
          </div>
          <div className={styles.routeBlock}>
            <FieldArray
              name="routePoints"
              component={FormRoutePoints}
            />
          </div>
          <div className={styles.passBlock}>
            <Field name="minPax"
                   component={FormNumberField}
                   max={6}
                   min={1}
                   inputProps={{
                     type: 'number',
                   }}
                   disabled={submitting}
                   label="Passengers"
                   className={styles.numberField}
            />
          </div>
          <div className={styles.checkboxes}>
            <Field
              name="pet"
              label="Pet"
              icon={<Paw />}
              component={FormToogleButtonField}
            />
            <Field
              name="package"
              label="Package"
              icon={<Package />}
              component={FormToogleButtonField}
            />
            <Field
              name="mask"
              label="Mask"
              icon={<Mask />}
              component={FormToogleButtonField}
            />
          </div>
        </div>
        <FormFooter>
          <Button type='submit'
                  onClick={() => dispatch(submit('reservation'))}
                  color="primary"
                  variant='contained'
                  disabled={submitting || !puPoint?.address?.zip || !doPoint?.address?.zip}
                  className={styles.button}
          >
            Choose a vehicle
          </Button>
        </FormFooter>
      </div>
    </form>
  );
});

const InitializeFromStateForm = reduxForm({
  form: 'reservation',
  enableReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  keepDirtyOnReinitialize: true,
  validate,
  onChange: (values, dispatch, props) => {
    if (props.error) dispatch(clearSubmitErrors('reservation'));
  },
})(RoutePoints);

const mapStateToProps = (state) => {
  const { client } = state.authorization;

  return {
    initialValues: {
      routePoints: [ {}, {} ],
      pet: false,
      mask: false,
      package: false,
      fullName: `${client?.firstName} ${client?.lastName}`,
      email: client.email,
      dateTime: 0,
      minPax: '1',
    },
    formData: getFormValues('reservation')(state),
    data: getFormMeta('reservation')(state),
  };
};

export default connect(mapStateToProps)(InitializeFromStateForm);
